import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

import { useUser } from '../../store/UserProvider';

import SEO from '../SEO';
import Popup from '../Popup';
import ActTags from './ActTags'; 
import Loading from "../Loading";
import PushPopup from './PushPopup'; // 제너럴 PushPopup 컴포넌트
import MyPageMenu from "./MyPageMenu";
import DeleteSelected from "./DeleteSelected";

import styleClasses from "./css/MyPage.module.scss";

function ContactList() {
  const navigate = useNavigate();

  const { user, userLoading } = useUser();

  const [isLoading, setIsLoading]               = useState(true);
  const [error, setError]                       = useState("");
  
  const [uid, setUid]                           = useState(null);
  const [contacts, setContacts]                 = useState([]);
  const [checkedIDs, setCheckedIDs]             = useState([]);
  const [selectedContact, setSelectedContact]   = useState([]);

  // 팝업 관련 상태
  const [openPopup, setOpenPopup]             = useState(false);
  const [popupTitle, setPopupTitle]           = useState("");
  const [popupButtonText, setPopupButtonText] = useState("");
  const [popupFunction, setPopupFunction]     = useState(() => {});
  const [buttonLoading, setButtonLoading]     = useState(false);
  const [openPushContactPopup, setOpenPushContactPopup] = useState(false);

  // 🔹 연락처 조회
  const fetchContacts = useCallback(async () => {
    setError('');
    try {
      const contactsRef = collection(db, "users_data", uid, "contact");
      const snapshot = await getDocs(contactsRef);
      const fetchedContacts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      // 이름 순으로 정렬 (옵션)
      fetchedContacts.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setContacts(fetchedContacts);
    } catch (err) {
      console.error("연락처 데이터를 불러오는 중 오류 발생:", err);
      setError("연락처 데이터를 불러오는 중 오류가 발생했습니다.");
    }
    setIsLoading(false);
  }, [uid]);

  // 🔹 연락처 조회 전 로그인 세션 확인
  useEffect(() => {
    if (userLoading) return;

    if (user?.userID) {
      setUid(user.userID);
      setIsLoading(true);
      fetchContacts();
    } else {
      navigate('/login');
    }
  }, [fetchContacts, navigate, user?.userID, userLoading]);

  // 🔹 모든 연락처 체크박스 선택/해제 처리
  const checkAllContacts = (event) => {
    let all = event.target.checked;
    const allCheckboxes = document.querySelectorAll('input[name=contact]');
    
    setCheckedIDs([]);
    allCheckboxes.forEach((checkbox) => {
      checkbox.checked = all;
      if (all) {
        setCheckedIDs((prev) => [...prev, checkbox.value]);
      }
    });
  };

  // 🔹 개별 연락처 체크 처리
  const checkContactID = (id) => {
    setCheckedIDs((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // 🔹 뮤지카드 적용 팝업 열기
  useEffect(() => {
    if (selectedContact.length) {
      setOpenPushContactPopup(true);
    }
  }, [selectedContact]);

  return (
    <>
      <SEO title='연락처' />
      <div className={styleClasses.myPage}>
        <div className={styleClasses.inner}>
          <div className={styleClasses.title}>
            <h1>Contact</h1>
          </div>
          <div className={styleClasses.editContent}>
            <div className={styleClasses.leftContent}>
              <MyPageMenu currentPage='contact' />
            </div>
            <div className={styleClasses.rightContent}>
            {
              error
              ?
              <p className={ styleClasses.error }>{ error }</p>
              :
              <>
                <div className={styleClasses.topButtons}>
                  <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                    <i className="questionBlack w20"></i>
                    <div className="toolTipText left">
                      <p>나의 연락처를 자유롭게 추가, 수정, 삭제하세요. <br />그리고 원하는 연락처를 골라 뮤지카드에 적용해보세요!</p>
                    </div>
                  </div>
                  <DeleteSelected
                    selectedIDs={ checkedIDs }
                    documents={contacts}
                    collectionPath={`users_data/${uid}/contact`}
                    userID={uid}
                    itemName="연락처"
                    subcollections={[]} // 추가 서브컬렉션이 필요하면 여기에 넣으세요.
                    onDeleteSuccess={() => setCheckedIDs([])}
                    fetchData={fetchContacts}
                    setOpenPopup={ setOpenPopup }
                    setButtonLoading={ setButtonLoading }
                    setPopupTitle={ setPopupTitle }
                    setPopupFunction={ setPopupFunction }
                    setPopupButtonText={ setPopupButtonText }
                  />
                  <div className="commonButton fullwidth">
                    <Link to="/my-page/contact/new" className="black">
                      추가
                    </Link>
                  </div>
                </div>
                {
                  isLoading
                  ?
                  (
                    <Loading />
                  )
                  :
                  (
                    <div className={styleClasses.activityList}>
                    <table>
                      <thead>
                        <tr className={ styleClasses.contactRow }>
                          <th>
                            <div className='commonForm'>
                              <div className="formRow">
                                <div className="fieldBox checkBox">
                                  <input
                                    type="checkbox"
                                    id='allContacts'
                                    name='allContacts'
                                    onChange={ checkAllContacts }
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                          <th>연락처</th>
                          <th>이메일</th>
                          <th>뮤지카드</th>
                        </tr>
                      </thead>
                      <tbody>
                      {
                        contacts.map((contact) => (
                          <tr key={contact.id} className={styleClasses.contactRow}>
                            <td className={styleClasses.checkBox}>
                              <div className="commonForm">
                                <div className="formRow">
                                  <div className="fieldBox checkBox">
                                    <input
                                      type="checkbox"
                                      name="contact"
                                      id={`contact_${contact.id}`}
                                      value={ contact.id }
                                      onChange={() => checkContactID(contact.id)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <Link to={`/my-page/contact/${contact.id}`} className="black">
                                {contact.name ? contact.name : "이름 없음"}
                              </Link>
                            </td>
                            <td>
                              {contact.email}
                            </td>
                            <td>
                              <ActTags
                                act={ contact.act || {} }
                                userUid={ uid }
                                attribute="contact"
                                albumID={ contact.id }
                                fetchData={ fetchContacts }
                                setSelectedItem={ setSelectedContact }
                                setOpenPopup={ setOpenPopup }
                                setButtonLoading={ setButtonLoading }
                                setPopupTitle={ setPopupTitle }
                                setPopupFunction={ setPopupFunction }
                                setPopupButtonText={ setPopupButtonText }
                              />
                            </td>
                          </tr>
                        ))
                      }
                      </tbody>
                    </table>
                    </div>
                  )
                }
              </>
            }
            </div>
          </div>
        </div>
      </div>
      <PushPopup
        open={ openPushContactPopup }
        setOpenPopup={ setOpenPushContactPopup }
        attribute="contact"
        selectedItem={ selectedContact }      // 단일 선택
        selectedItems={ checkedIDs }          // 다중 선택
        setSelectedItem={ setSelectedContact }// 단일 선택 초기화
        clearSelectedItems={ setCheckedIDs }  // 다중 선택 초기화 함수 (필요하면)
        categoryOptions={ [] }
        defaultCategory="stages"
        fetchData={ fetchContacts }
        setOpenAlertPopup={ setOpenPopup }
        setPopupTitle={ setPopupTitle }
        setPopupButtonText={ setPopupButtonText }
        setPopupFunction={ setPopupFunction }
      />
      <Popup
          open={openPopup}
          setOpenPopup={setOpenPopup}
          title={popupTitle}
          text=""
          buttonText={popupButtonText}
          buttonFunction={popupFunction}
          buttonLoading={buttonLoading}
        />
    </>
  );
}

export default ContactList;